import { WarningCircleIcon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { match, P } from 'ts-pattern'
import { type ConnectionStatus } from '../../hooks/use-sse-stream.ts'
import { type AnalysisStreamEvent } from '../../utils/api-client/sse-schemas.ts'
import { formatDate } from '../../utils/date-utils.ts'
import { ActivityFeed } from './activity-feed.tsx'
import * as styles from './analysis-activity-tab.css.ts'
import { eventsToSteps } from './events-to-steps.ts'

type ActivityFeedMetadataProps = {
  startDate: string
  duration: string
}

export type AnalysisActivityTabProps = {
  events: AnalysisStreamEvent[]
  connectionStatus: ConnectionStatus
  error?: Error
  overlay?: React.ReactNode
  metadata?: ActivityFeedMetadataProps
  progressLabel?: string
  isLive: boolean
}

export const AnalysisActivityTab: React.FC<AnalysisActivityTabProps> = ({
  events,
  connectionStatus,
  error,
  overlay,
  metadata,
  progressLabel,
  isLive,
}) => {
  return match({ connectionStatus, hasEvents: events.length > 0, error })
    .with({ connectionStatus: 'error', error: P.not(P.nullish) }, ({ error }) => (
      <div className={styles.tabContainer}>
        <div className={styles.errorState}>
          <WarningCircleIcon size={20} weight="light" />
          Failed to connect to activity stream: {error.message}
        </div>
      </div>
    ))
    .with({ connectionStatus: P.union('idle', 'finished'), hasEvents: false }, () => (
      <div className={styles.tabContainer}>
        <div className={styles.emptyState}>No activity data available</div>
      </div>
    ))
    .with({ connectionStatus: 'connecting', hasEvents: false }, () => (
      <div className={styles.tabContainer}>
        <ActivityFeed steps={[]} isLive={isLive} progressLabel={progressLabel ?? 'analysis'} overlay={overlay} />
      </div>
    ))
    .otherwise(() => (
      <ActivityFeedWithEvents
        events={events}
        connectionStatus={connectionStatus}
        overlay={overlay}
        metadata={metadata}
        progressLabel={progressLabel}
        isLive={isLive}
      />
    ))
}

type ActivityFeedWithEventsProps = {
  events: AnalysisStreamEvent[]
  connectionStatus: ConnectionStatus
  overlay?: React.ReactNode
  metadata?: ActivityFeedMetadataProps
  progressLabel?: string
  isLive: boolean
}

const ActivityFeedWithEvents: React.FC<ActivityFeedWithEventsProps> = ({
  events,
  connectionStatus,
  overlay,
  metadata,
  progressLabel,
  isLive,
}) => {
  const isComplete = connectionStatus === 'finished'

  const progressEvents = useMemo(
    () => events.filter(e => e.type === 'data-progress-finding').map(e => e.content),
    [events]
  )

  const hasCompleted = useMemo(() => events.some(e => e.type === 'data-progress-finding-complete'), [events])

  const steps = useMemo(() => eventsToSteps(progressEvents, hasCompleted, false), [progressEvents, hasCompleted])

  const lastProgress = progressEvents[progressEvents.length - 1]
  const resolvedProgressLabel = progressLabel ?? lastProgress?.title ?? 'Analyzing'

  return (
    <div className={styles.tabContainer}>
      {metadata && <ActivityFeedMetadata {...metadata} />}
      <ActivityFeed
        steps={steps}
        progressLabel={isComplete ? undefined : resolvedProgressLabel}
        isComplete={isComplete}
        completedEvents={lastProgress?.completed_events}
        totalEvents={lastProgress?.total_events}
        overlay={overlay}
        isLive={isLive}
      />
    </div>
  )
}

const ActivityFeedMetadata: React.FC<ActivityFeedMetadataProps> = ({ startDate, duration }) => {
  const date = formatDate({ date: startDate, type: 'mm/dd/yy' })
  const time = formatDate({ date: startDate, type: 'time-with-zone' })

  return (
    <div className={styles.metadataContainer}>
      <div className={styles.metadataGroup}>
        <span className={styles.metadataLabel}>Start</span>
        <span className={styles.metadataValue}>{date}</span>
        <span className={styles.metadataValue}>{time}</span>
      </div>
      <div className={styles.metadataGroup}>
        <span className={styles.metadataLabel}>Duration</span>
        <span className={styles.metadataValue}>{duration}</span>
      </div>
    </div>
  )
}
